/**
 * LockStatusPicker — the hold-type (soft reservation / option) choice plus
 * the required shared expiry-date control, extracted out of the retired
 * lock-reservations-dialog.tsx so the merged "Zarezerwuj wstępnie" dialog
 * (reserve-preliminary-dialog.tsx) can reuse it verbatim,
 * unchanged, including its expiry validation — offer-accept-reservation /
 * "Preliminary reservation action in the offer workspace finalization
 * panel": "Keep the existing hold-type choice ... and the required shared
 * expiry date exactly as lock-reservations-dialog.tsx implements them
 * today".
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@ooh/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@ooh/ui/components/radio-group';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { toDateStr } from '@ooh/ui/lib/date-string';
import { pl } from 'date-fns/locale';
import { addCalendarDays, TTL_HORIZON_DAYS } from '@ooh/contracts';
import type { LockReservationsRequest } from '@ooh/contracts';

export type LockStatus = LockReservationsRequest['status'];

export function LockStatusPicker({
  status,
  onStatusChange,
  ttlDateStr,
  onTtlDateChange,
}: {
  status: LockStatus;
  onStatusChange: (status: LockStatus) => void;
  ttlDateStr: string;
  onTtlDateChange: (next: string) => void;
}) {
  const { t } = useTranslation('offers');
  const maxDateStr = toDateStr(addCalendarDays(new Date(), TTL_HORIZON_DAYS));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('workspace.versions.lock.dialog.description')}
      </p>
      <div className="space-y-2">
        <Label>{t('workspace.versions.lock.dialog.statusLabel')}</Label>
        <RadioGroup value={status} onValueChange={(value) => onStatusChange(value as LockStatus)}>
          <RadioGroupItem
            value="SOFT_RESERVATION"
            label={t('workspace.versions.lock.dialog.statusSoftReservation')}
            data-testid="lock-status-soft-reservation"
          />
          <RadioGroupItem
            value="OPTION"
            label={t('workspace.versions.lock.dialog.statusOption')}
            data-testid="lock-status-option"
          />
        </RadioGroup>
        {status === 'OPTION' ? (
          <p className="text-xs text-muted-foreground">
            {t('workspace.versions.lock.dialog.optionNeverQueuesHint')}
          </p>
        ) : null}
      </div>
      <div className="space-y-2">
        <Label htmlFor="lock-reservations-ttl-date">
          {t('workspace.versions.lock.dialog.sharedExpiryLabel')}
        </Label>
        <DatePickerButton
          id="lock-reservations-ttl-date"
          value={ttlDateStr}
          onChange={onTtlDateChange}
          locale={pl}
          testId="lock-reservations-ttl-input"
          minDate={toDateStr(new Date())}
          maxDate={maxDateStr}
          required
        />
        <p className="text-xs text-muted-foreground">
          {t('workspace.versions.lock.dialog.sharedExpiryHint')}
        </p>
      </div>
    </div>
  );
}
